import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const sections = [
  {
    id: 'purpose',
    title: '1. Purpose & Scope',
    content: `This Acceptable Use Policy ("AUP") governs the use of the Quali5d AI Recruitment Intelligence Platform ("Platform") operated by Uni5d Technology Solutions LLC. This AUP is incorporated by reference into the Terms of Service and applies to all Customers, users, and any person accessing the Platform.

By using the Platform, you agree to comply with this AUP. Violation of this AUP may result in immediate suspension or termination of your account without refund.

Effective Date: May 4, 2026`
  },
  {
    id: 'permitted',
    title: '2. Permitted Uses',
    content: `The Platform is designed and licensed exclusively for the following legitimate business purposes:

Recruitment & Talent Acquisition:
• Processing job descriptions to generate AI-powered recruiter documentation
• Conducting AI-assisted intake meetings with hiring managers
• Scheduling and conducting AI-powered candidate screening interviews
• Generating and reviewing AI candidate assessments as part of a human-led hiring process

Organizational Use:
• Use by authorized employees and contractors of the subscribing organization
• Use within the jurisdiction(s) disclosed during registration
• Use in compliance with all applicable employment, privacy, and AI regulation laws

The Platform is a B2B tool intended for use by HR professionals, recruiters, talent acquisition teams, and hiring managers within registered organizations.`
  },
  {
    id: 'prohibited',
    title: '3. Prohibited Uses',
    content: `You may not use the Platform for any of the following purposes:

3.1 Illegal & Discriminatory Hiring Practices
• Using AI-generated scores or recommendations as the sole basis for employment decisions without human review
• Using the Platform to discriminate against candidates based on race, color, religion, gender, sexual orientation, national origin, age, disability, or any other protected characteristic
• Conducting candidate screening that violates applicable employment law, including the Equal Employment Opportunity laws (US), Equality Act (UK), or equivalent legislation in your jurisdiction
• Using the Platform in jurisdictions where AI-assisted hiring is prohibited without complying with local requirements (e.g., NYC Local Law 144 bias audit requirements, EU AI Act obligations)

3.2 Unauthorized Data Collection & Privacy Violations
• Scheduling a candidate for an AI interview without their explicit informed consent that the interview will be conducted by an AI and will be recorded
• Uploading personal data of individuals without a lawful basis for processing
• Using the Platform to process special categories of sensitive data (health, biometric, political opinions, etc.) without explicit consent
• Collecting or storing candidate data beyond what is necessary for legitimate recruitment purposes
• Sharing candidate data with unauthorized third parties

3.3 Platform Abuse
• Attempting to reverse engineer, decompile, or access the underlying AI models, algorithms, or source code
• Conducting security testing, penetration testing, or vulnerability scanning without prior written authorization from Uni5d
• Using automated scripts, bots, or scrapers to access or extract Platform data
• Circumventing usage limits, credit systems, or subscription restrictions
• Sharing login credentials between multiple users or organizations
• Reselling, sublicensing, or providing Platform access to third parties

3.4 Harmful Content
• Uploading malware, viruses, or malicious code
• Uploading content that infringes intellectual property rights
• Using the Platform to harass, threaten, or harm any individual

3.5 Misrepresentation
• Impersonating another company or individual during registration
• Providing false company information, tax registration numbers, or billing details
• Using the Platform for purposes materially different from those disclosed during registration`
  },
  {
    id: 'candidate-consent',
    title: '4. Candidate Consent Requirements',
    content: `This section is critically important. Before scheduling any candidate for a Phase 3 AI interview, you must:

Required Disclosures to Candidates:
1. Inform the candidate that the interview will be conducted by an AI system (not a human interviewer)
2. Inform the candidate that the interview will be audio/video recorded
3. Inform the candidate that their responses will be analyzed by AI to generate a score and assessment
4. Provide the candidate with your organization's privacy policy or data handling notice
5. Obtain explicit, informed consent before the interview begins

Consent Must Be:
• Freely given — candidates must be able to decline without adverse consequences to their application
• Specific — relating specifically to the AI interview
• Informed — candidates must understand what they are consenting to
• Documented — you must maintain records of consent

Jurisdiction-Specific Requirements:
• New York City: You must conduct an annual bias audit of AI hiring tools and post results publicly before using them for NYC-based candidates (Local Law 144)
• Illinois: You must notify candidates before using AI video interview tools (AIVIPA)
• EU/EEA: Candidates have the right to request human review of any automated decision (GDPR Article 22)
• Other jurisdictions: Comply with all applicable local AI and employment screening laws

Failure to obtain proper candidate consent is a material breach of this AUP and the Terms of Service, and Uni5d bears no liability for any claims arising from your failure to comply.`
  },
  {
    id: 'compliance',
    title: '5. Legal & Regulatory Compliance',
    content: `5.1 Customer Responsibility

You are solely responsible for ensuring your use of the Platform complies with all applicable laws and regulations in your jurisdiction and in the jurisdictions where candidates are located, including but not limited to:

Employment Laws:
• Equal employment opportunity and anti-discrimination laws
• Fair Credit Reporting Act (FCRA) — if AI assessments are used as consumer reports
• State and local AI employment screening regulations

Data Protection Laws:
• General Data Protection Regulation (GDPR) — EU/EEA
• UK GDPR and Data Protection Act 2020
• California Consumer Privacy Act (CCPA/CPRA)
• India Digital Personal Data Protection Act (DPDP)
• Other applicable national and state data protection laws

AI Regulation:
• EU AI Act (high-risk AI system requirements for employment screening)
• NYC Local Law 144
• Illinois Artificial Intelligence Video Interview Act
• Colorado AI Act
• Any other applicable AI regulation in your jurisdiction

5.2 Data Processing Agreement

For Customers subject to GDPR who process EU/EEA personal data using the Platform, Uni5d acts as a data processor and you act as the data controller. A Data Processing Agreement (DPA) is available upon request at admin@uni5d.ai and is incorporated by reference into the Terms of Service.

5.3 Audit Rights

You must maintain records sufficient to demonstrate compliance with this AUP and applicable law. Uni5d reserves the right to request reasonable evidence of compliance in cases where violations are suspected.`
  },
  {
    id: 'monitoring',
    title: '6. Monitoring & Enforcement',
    content: `6.1 We reserve the right to monitor Platform usage for compliance with this AUP, security threats, and system integrity. Monitoring is conducted in accordance with our Privacy Policy.

6.2 Violations. Upon discovering an AUP violation, we may, at our sole discretion:
• Issue a warning and require remediation
• Temporarily suspend your account
• Permanently terminate your account without refund
• Report illegal activity to appropriate law enforcement authorities

6.3 Reporting. If you become aware of AUP violations by other Platform users or have security concerns, please report them to admin@uni5d.ai.

6.4 No Obligation to Monitor. Uni5d has no obligation to monitor Customer Data or usage for AUP compliance. Responsibility for lawful use rests entirely with the Customer.`
  },
  {
    id: 'updates',
    title: '7. Updates to This Policy',
    content: `We may update this AUP at any time to reflect changes in law, technology, or Platform capabilities. Material changes will be communicated via email to registered account holders at least 14 days before taking effect.

Continued use of the Platform after the effective date of changes constitutes acceptance of the revised AUP.

For questions about this AUP, contact us at admin@uni5d.ai.

Uni5d Technology Solutions LLC
Mooresville, North Carolina
EIN: 42-1896513
admin@uni5d.ai`
  }
];

export default function AcceptableUsePage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('purpose');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Acceptable Use Policy — Quali5d AI Recruitment Platform</title>
        <meta name="description" content="Acceptable Use Policy for Quali5d. Rules for lawful and ethical use, candidate consent requirements, prohibited activities, and compliance with employment law." />
        <link rel="canonical" href="https://www.quali5d.com/acceptable-use" />
        <meta property="og:title" content="Acceptable Use Policy — Quali5d AI Recruitment Platform" />
        <meta property="og:description" content="Acceptable Use Policy for Quali5d. Rules for lawful and ethical use, candidate consent requirements, prohibited activities, and compliance with employment law." />
        <meta property="og:url" content="https://www.quali5d.com/acceptable-use" />
        <meta name="twitter:title" content="Acceptable Use Policy — Quali5d AI Recruitment Platform" />
        <meta name="twitter:description" content="Acceptable Use Policy for Quali5d. Rules for lawful and ethical use, candidate consent requirements, prohibited activities, and compliance with employment law." />
      </Helmet>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            <span className="text-sm font-medium">Back to Quali5d</span>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-xs">Q5</span>
            </div>
            <span className="font-bold text-gray-900 text-sm">Quali5d</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-full px-4 py-1.5 mb-4">
            <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0110 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 10-2 0v3a1 1 0 102 0V7z" clipRule="evenodd"/>
            </svg>
            <span className="text-xs font-semibold text-purple-700">Legal Document</span>
          </div>
          <h1 className="text-4xl font-black text-gray-900 mb-3">Acceptable Use Policy</h1>
          <p className="text-gray-500 text-lg">Rules governing lawful and ethical use of the Quali5d Platform</p>
          <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-500">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-green-500 rounded-full"></span>Effective: May 4, 2026</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-red-500 rounded-full"></span>Violations may result in termination</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-blue-500 rounded-full"></span>Candidate consent required</span>
          </div>
        </div>

        {/* Critical notice */}
        <div className="mb-8 bg-red-50 border-2 border-red-200 rounded-2xl p-6">
          <div className="flex gap-3">
            <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
            </svg>
            <div>
              <p className="font-bold text-red-800 mb-1">Important: Candidate Consent Required</p>
              <p className="text-sm text-red-700">Before scheduling any candidate for an AI interview, you must obtain their explicit informed consent that the interview will be conducted by an AI and will be recorded. See Section 4 for full requirements. Failure to obtain consent is a material breach of this Policy.</p>
            </div>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Sidebar TOC */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 bg-white rounded-2xl border border-gray-200 p-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 px-2">Contents</p>
              <nav className="space-y-0.5">
                {sections.map(s => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    onClick={() => setActiveSection(s.id)}
                    className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeSection === s.id
                        ? 'bg-purple-50 text-purple-700 font-semibold'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="space-y-6">
              {sections.map(s => (
                <div
                  key={s.id}
                  id={s.id}
                  className="bg-white rounded-2xl border border-gray-200 p-8"
                  onMouseEnter={() => setActiveSection(s.id)}
                >
                  <h2 className="text-xl font-bold text-gray-900 mb-4 pb-3 border-b border-gray-100">{s.title}</h2>
                  <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{s.content}</div>
                </div>
              ))}
            </div>

            <div className="mt-6 text-center text-sm text-gray-400">
              © 2026 Uni5d Technology Solutions LLC · EIN: 42-1896513 · Mooresville, North Carolina<br/>
              Questions? <a href="mailto:admin@uni5d.ai" className="text-blue-600 hover:underline">admin@uni5d.ai</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}